import React, { useState, useEffect } from 'react';
import { PlusIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import DataTable from '../components/common/DataTable';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import { employeeService, departmentService, positionService } from '../services/hrService';
import { getStatusColor } from '../utils/format';

const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    employee_no: '',
    first_name: '',
    middle_name: '',
    last_name: '',
    email: '',
    mobile: '',
    birth_date: '',
    gender: 'M',
    civil_status: 'Single',
    department: '',
    position: '',
    employment_status: 'Probationary',
    employment_type: 'Full-Time',
    salary_type: 'Daily',
    date_hired: '',
    basic_salary: '',
    daily_rate: '',
    hourly_rate: '',
    sss_no: '',
    philhealth_no: '',
    pagibig_no: '',
    tin: '',
    emergency_contact_name: '',
    emergency_contact_number: '',
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const fetchData = async () => {
    try {
      const [empRes, deptRes, posRes] = await Promise.all([
        employeeService.getAll({ search: searchTerm }),
        departmentService.getAll(),
        positionService.getAll(),
      ]);
      setEmployees(empRes.data.results || empRes.data);
      setDepartments(deptRes.data.results || deptRes.data);
      setPositions(posRes.data.results || posRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Auto-calculate salaries when daily_rate changes
    if (name === 'daily_rate' && value) {
      const dailyRate = parseFloat(value);
      if (!isNaN(dailyRate)) {
        // DOLE 5-day work week: Monthly = Daily × 261/12 (21.75 days/month), 8 hrs/day
        const hourlyRate = (dailyRate / 8).toFixed(2);
        const basicSalary = (dailyRate * 21.75).toFixed(2);
        setFormData(prev => ({
          ...prev,
          [name]: value,
          hourly_rate: hourlyRate,
          basic_salary: basicSalary
        }));
        return;
      }
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Build submitData - only include fields that exist in the backend model
      const submitData = {
        employee_no: String(formData.employee_no || ''),
        first_name: String(formData.first_name || ''),
        middle_name: String(formData.middle_name || ''),
        last_name: String(formData.last_name || ''),
        suffix: String(formData.suffix || ''),
        email: String(formData.email || ''),
        mobile: String(formData.mobile || ''),
        birth_date: formData.birth_date || null,
        gender: String(formData.gender || 'M'),
        civil_status: String(formData.civil_status || 'Single'),
        employment_status: String(formData.employment_status || 'Probationary'),
        employment_type: String(formData.employment_type || 'Full-Time'),
        salary_type: String(formData.salary_type || 'Daily'),
        date_hired: String(formData.date_hired || ''),
        basic_salary: parseFloat(formData.basic_salary || 0),
        daily_rate: formData.daily_rate ? parseFloat(formData.daily_rate) : null,
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
        sss_no: String(formData.sss_no || ''),
        philhealth_no: String(formData.philhealth_no || ''),
        pagibig_no: String(formData.pagibig_no || ''),
        tin: String(formData.tin || ''),
        emergency_contact_name: String(formData.emergency_contact_name || ''),
        emergency_contact_number: String(formData.emergency_contact_number || ''),
        department_id: formData.department ? String(formData.department) : null,
        position_id: formData.position ? String(formData.position) : null,
      };

      if (editingEmployee) {
        await employeeService.update(editingEmployee.id, submitData);
      } else {
        await employeeService.create(submitData);
      }
      setIsModalOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving employee:', error);
      alert(error.response?.data?.detail || JSON.stringify(error.response?.data) || 'Failed to save employee');
    }
  };

  const handleEdit = async (employee) => {
    try {
      // Fetch full employee details
      const response = await employeeService.getById(employee.id);
      const fullEmployee = response.data;

      setEditingEmployee(fullEmployee);
      setFormData({
        employee_no: fullEmployee.employee_no || '',
        first_name: fullEmployee.first_name || '',
        middle_name: fullEmployee.middle_name || '',
        last_name: fullEmployee.last_name || '',
        suffix: fullEmployee.suffix || '',
        email: fullEmployee.email || '',
        mobile: fullEmployee.mobile || '',
        birth_date: fullEmployee.birth_date || '',
        gender: fullEmployee.gender || 'M',
        civil_status: fullEmployee.civil_status || 'Single',
        department: fullEmployee.department?.id || '',
        position: fullEmployee.position?.id || '',
        employment_status: fullEmployee.employment_status || 'Probationary',
        employment_type: fullEmployee.employment_type || 'Full-Time',
        salary_type: fullEmployee.salary_type || 'Daily',
        date_hired: fullEmployee.date_hired || '',
        basic_salary: fullEmployee.basic_salary || '',
        daily_rate: fullEmployee.daily_rate || '',
        hourly_rate: fullEmployee.hourly_rate || '',
        sss_no: fullEmployee.sss_no || '',
        philhealth_no: fullEmployee.philhealth_no || '',
        pagibig_no: fullEmployee.pagibig_no || '',
        tin: fullEmployee.tin || '',
        emergency_contact_name: fullEmployee.emergency_contact_name || '',
        emergency_contact_number: fullEmployee.emergency_contact_number || '',
      });
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error fetching employee details:', error);
      alert('Failed to load employee details');
    }
  };

  const handleToggleActive = async (employee) => {
    const action = employee.is_active ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} ${employee.full_name || 'this employee'}?`)) return;
    try {
      await employeeService.patch(employee.id, { is_active: !employee.is_active });
      fetchData();
    } catch (error) {
      console.error(`Error ${action} employee:`, error);
      alert(error.response?.data?.detail || JSON.stringify(error.response?.data) || `Failed to ${action} employee`);
    }
  };

  const resetForm = () => {
    setEditingEmployee(null);
    setFormData({
      employee_no: '',
      first_name: '',
      middle_name: '',
      last_name: '',
      suffix: '',
      email: '',
      mobile: '',
      birth_date: '',
      gender: 'M',
      civil_status: 'Single',
      department: '',
      position: '',
      employment_status: 'Probationary',
      employment_type: 'Full-Time',
      salary_type: 'Daily',
      date_hired: '',
      basic_salary: '',
      daily_rate: '',
      hourly_rate: '',
      sss_no: '',
      philhealth_no: '',
      pagibig_no: '',
      tin: '',
      emergency_contact_name: '',
      emergency_contact_number: '',
    });
  };

  const columns = [
    { header: 'Employee No', accessor: 'employee_no' },
    {
      header: 'Name',
      render: (row) => row.full_name || `${row.first_name} ${row.last_name}`
    },
    { header: 'Department', accessor: 'department' },
    { header: 'Position', accessor: 'position' },
    { header: 'Employment Status', accessor: 'employment_status' },
    { header: 'Basic Salary', accessor: 'basic_salary', render: (row) => row.basic_salary ? `₱${parseFloat(row.basic_salary).toLocaleString('en-PH', { minimumFractionDigits: 2 })}` : '—' },
    {
      header: 'Status',
      render: (row) => (
        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(row.is_active ? 'Active' : 'Inactive')}`}>
          {row.is_active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      header: 'Actions',
      render: (row) => (
        <div className="flex gap-2">
          <button
            onClick={() => handleEdit(row)}
            className="text-primary-600 hover:text-primary-900"
          >
            Edit
          </button>
          <button
            onClick={() => handleToggleActive(row)}
            className={row.is_active ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}
          >
            {row.is_active ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      ),
    },
  ];

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div>
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Employees</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage employee records and information
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setIsModalOpen(true);
          }}
          className="mt-4 sm:mt-0"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Employee
        </Button>
      </div>

      <div className="mb-4">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search employees..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <DataTable columns={columns} data={employees} />
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          resetForm();
        }}
        title={editingEmployee ? 'Edit Employee' : 'Add New Employee'}
        size="xl"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Employee No *</label>
              <input
                type="text"
                name="employee_no"
                value={formData.employee_no}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">First Name *</label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Middle Name</label>
              <input
                type="text"
                name="middle_name"
                value={formData.middle_name}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Last Name *</label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Mobile</label>
              <input
                type="text"
                name="mobile"
                value={formData.mobile}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Emergency Contact Name</label>
              <input
                type="text"
                name="emergency_contact_name"
                value={formData.emergency_contact_name}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Emergency Contact Number</label>
              <input
                type="text"
                name="emergency_contact_number"
                value={formData.emergency_contact_number}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Birth Date</label>
              <input
                type="date"
                name="birth_date"
                value={formData.birth_date}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Gender</label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="M">Male</option>
                <option value="F">Female</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Civil Status</label>
              <select
                name="civil_status"
                value={formData.civil_status}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="Single">Single</option>
                <option value="Married">Married</option>
                <option value="Widowed">Widowed</option>
                <option value="Separated">Separated</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Department</label>
              <select
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select Department</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Position</label>
              <select
                name="position"
                value={formData.position}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select Position</option>
                {positions.map(pos => (
                  <option key={pos.id} value={pos.id}>{pos.title}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Employment Status *</label>
              <select
                name="employment_status"
                value={formData.employment_status}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="Probationary">Probationary</option>
                <option value="Regular">Regular</option>
                <option value="Project-Based">Project-Based</option>
                <option value="Casual">Casual</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Employment Type *</label>
              <select
                name="employment_type"
                value={formData.employment_type}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="Full-Time">Full-Time</option>
                <option value="Part-Time">Part-Time</option>
                <option value="Contractual">Contractual</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Salary Type *</label>
              <select
                name="salary_type"
                value={formData.salary_type}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="Monthly">Monthly</option>
                <option value="Semi-Monthly">Semi-Monthly</option>
                <option value="Daily">Daily</option>
                <option value="Hourly">Hourly</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Date Hired *</label>
              <input
                type="date"
                name="date_hired"
                value={formData.date_hired}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Basic Salary (auto-calculated)</label>
              <input
                type="number"
                name="basic_salary"
                value={formData.basic_salary}
                readOnly
                step="0.01"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-100 text-gray-600 cursor-not-allowed"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Daily Rate</label>
              <input
                type="number"
                name="daily_rate"
                value={formData.daily_rate}
                onChange={handleInputChange}
                step="0.01"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Hourly Rate (auto-calculated)</label>
              <input
                type="number"
                name="hourly_rate"
                value={formData.hourly_rate}
                readOnly
                step="0.01"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-100 text-gray-600 cursor-not-allowed"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">SSS No</label>
              <input
                type="text"
                name="sss_no"
                value={formData.sss_no}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">PhilHealth No</label>
              <input
                type="text"
                name="philhealth_no"
                value={formData.philhealth_no}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Pag-IBIG No</label>
              <input
                type="text"
                name="pagibig_no"
                value={formData.pagibig_no}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">TIN</label>
              <input
                type="text"
                name="tin"
                value={formData.tin}
                onChange={handleInputChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsModalOpen(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button type="submit">
              {editingEmployee ? 'Update' : 'Create'} Employee
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Employees;
